import React, { createContext, useState, useEffect, useCallback } from 'react';
import { ethers } from 'ethers';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';

// Create the context
export const WalletContext = createContext();

export const WalletProvider = ({ children }) => {
  const { currentUser, updateWalletAddress } = useAuth();
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [isMetaMaskInstalled, setIsMetaMaskInstalled] = useState(false);

  // Check if MetaMask is installed
  useEffect(() => {
    const checkMetaMaskInstalled = async () => {
      if (window.ethereum) {
        setIsMetaMaskInstalled(true);
      }
    };

    checkMetaMaskInstalled();
  }, []);

  // Function to handle account changes
  const handleAccountsChanged = useCallback((accounts) => {
    if (accounts.length === 0) {
      // User has disconnected their account
      setAccount(null);
      setSigner(null);
      console.log('Please connect to MetaMask.');
    } else if (accounts[0] !== account) {
      // User has switched accounts
      setAccount(accounts[0]);
      if (provider) {
        const newSigner = provider.getSigner();
        setSigner(newSigner);
      }
    }
  }, [account, provider]);

  // Function to handle chain changes
  const handleChainChanged = useCallback((_chainId) => {
    // Convert from hex to decimal
    const newChainId = parseInt(_chainId, 16);
    setChainId(newChainId);

    // Reload the page when chain changes as recommended by MetaMask
    window.location.reload();
  }, []);

  // Set up event listeners
  useEffect(() => {
    if (window.ethereum) {
      // Listen for account changes
      window.ethereum.on('accountsChanged', handleAccountsChanged);

      // Listen for chain changes
      window.ethereum.on('chainChanged', handleChainChanged);

      // Clean up listeners on unmount
      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
        window.ethereum.removeListener('chainChanged', handleChainChanged);
      };
    }
  }, [handleAccountsChanged, handleChainChanged]);

  // Function to connect wallet
  const connectWallet = async () => {
    if (!window.ethereum) {
      setError('MetaMask is not installed. Please install MetaMask to use this application.');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      // Request account access
      const accounts = await window.ethereum.request({ method: 'eth_requestAccounts' });

      // Create ethers provider
      const ethersProvider = new ethers.BrowserProvider(window.ethereum);
      setProvider(ethersProvider);

      // Get the network
      const network = await ethersProvider.getNetwork();
      setChainId(Number(network.chainId));

      // Get the signer
      const ethersSigner = await ethersProvider.getSigner();
      setSigner(ethersSigner);

      // Set the account
      setAccount(accounts[0]);

      // Update wallet address in user profile if logged in
      if (currentUser) {
        await updateWalletAddress(accounts[0]);
      }
    } catch (err) {
      console.error('Error connecting to MetaMask:', err);
      setError(err.message || 'Failed to connect to wallet');
    } finally {
      setIsConnecting(false);
    }
  };

  // Function to disconnect wallet
  const disconnectWallet = () => {
    setAccount(null);
    setSigner(null);
    // Note: MetaMask doesn't support programmatic disconnection,
    // so this just clears our local state
  };

  // Format address for display (0x1234...5678)
  const formatAddress = (address) => {
    if (!address) return '';
    return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
  };

  // Check if wallet is connected on page load
  useEffect(() => {
    const checkConnection = async () => {
      if (window.ethereum) {
        try {
          // Check if already connected
          const accounts = await window.ethereum.request({ method: 'eth_accounts' });
          if (accounts.length > 0) {
            // User is already connected, set up provider and signer
            const ethersProvider = new ethers.BrowserProvider(window.ethereum);
            setProvider(ethersProvider);

            const network = await ethersProvider.getNetwork();
            setChainId(Number(network.chainId));

            const ethersSigner = await ethersProvider.getSigner();
            setSigner(ethersSigner);

            setAccount(accounts[0]);
          }
        } catch (err) {
          console.error('Error checking wallet connection:', err);
        }
      }
    };

    checkConnection();
  }, []);

  // Context value
  const value = {
    account,
    provider,
    signer,
    chainId,
    isConnecting,
    error,
    isMetaMaskInstalled,
    connectWallet,
    disconnectWallet,
    formatAddress
  };

  return (
    <WalletContext.Provider value={value}>
      {children}
    </WalletContext.Provider>
  );
};

// Custom hook to use the wallet context
export const useWallet = () => {
  const context = React.useContext(WalletContext);
  if (context === undefined) {
    throw new Error('useWallet must be used within a WalletProvider');
  }
  return context;
};
